<template>
  <div class="section__row">
    <BasicText
      size="body-m"
      color="secondary-900"
    >
      Retrouvez ici les coordonnées de votre gestionnaire sur votre courrier d'accord de prise en charge, de refus ou de
      relance de votre dossier. Sélectionnez un rattachement pour y afficher les contacts associés.
      <br />
      Vous pouvez sélectionner jusqu'à {{ MAX_FAVORIS }} contacts à mettre en avant sur votre tableau de bord.
    </BasicText>
  </div>
  <div class="flex flex-column flex-gap-6">
    <div class="flex flex-gap-8 flex-wrap">
      <WrapperDropdown
        v-model="rattachementId"
        label="Compte sélectionné"
        :items="dropdownRattachements"
        style="min-width: 150px"
      />
      <WrapperInput
        v-model="contactModèle"
        label="Rechercher un contact rattaché à cet établissement"
        placeholder="Nom, prénom, Siren de l'entreprise, etc"
        alertType="info"
        iconState="iconLeft"
        iconName="search"
        deletable
        style="min-width: 150px"
      />
    </div>
    <div class="contacts-grid">
      <BasicCard
        v-for="card in contactsAsCards"
        v-bind="card"
        :key="`${card.title}_${card.subtitles}`"
        show-favoris
      />
    </div>
  </div>

  <Teleport to="main">
    <ModalComponent v-model="showMaxFavorisModal">
      <template #header>Limite atteinte</template>
      <template #content>
        <div class="flex flex-column flex-gap-4">
          <BasicText>Vous avez déjà sélectionné le nombre maximal de {{ MAX_FAVORIS }} contacts favoris.</BasicText>
          <BasicText>Veuillez en retirer un pour en ajouter un autre.</BasicText>
        </div>
      </template>
    </ModalComponent>
  </Teleport>
</template>

<script setup lang="ts">
  import { ref, computed, onBeforeMount } from 'vue'
  import { storeToRefs } from 'pinia'
  import { useContactStore } from './useContactsStore'
  import { useContactsFavorisStore, type ContactAvecFavoris } from './useContactsFavorisStore'
  import ModalComponent from '@/features/interface/modal/ModalComponent.vue'
  import { useContactCardMapping } from './useContactCardMapping'

  const { mapContactToCard } = useContactCardMapping()

  const contactStore = useContactStore()
  const { ajouteContactAuxFavoris, supprimerContactDesFavoris, MAX_FAVORIS } = useContactsFavorisStore()
  const { contactsFiltrés, contactModèle, rattachements, rattachementModel } = storeToRefs(contactStore)

  const rattachementId = computed({
    get: () => rattachementModel.value?.identifiantEntiteJuridique,
    set: (id: string) => {
      rattachementModel.value = rattachements.value?.find((item) => item.identifiantEntiteJuridique === id)
    },
  })

  const dropdownRattachements = computed(
    () =>
      rattachements.value?.map((rattachement) => ({
        id: rattachement.identifiantEntiteJuridique,
        label: `${rattachement.entite.denomination} - ${rattachement.entite.siren}`,
      })) ?? [],
  )

  const showMaxFavorisModal = ref(false)

  async function handleAjoutFavoris(email: string) {
    const isAdded = await ajouteContactAuxFavoris(email)
    if (!isAdded) {
      showMaxFavorisModal.value = true
    }
  }

  const contactsAsCards = computed(() => {
    return contactsFiltrés.value.map((contact: ContactAvecFavoris) => {
      const cardData = mapContactToCard(contact)
      return {
        ...cardData,
        onToggleFavoris: () =>
          contact.estFavoris ? supprimerContactDesFavoris(contact.email!) : handleAjoutFavoris(contact.email!),
      }
    })
  })

  onBeforeMount(async () => {
    await contactStore.chargerContacts()
  })
</script>

<style scoped lang="less">
  @import '../index.less';

  .contacts-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: @spacing-15;
  }

  @container (max-width: @breakpoint-mobile) {
    .contacts-grid {
      display: flex;
      flex-direction: column;
    }
  }
</style>

